import {
  isPrivyEmbeddedWallet,
  sortPrivyWalletsByPriority,
} from "@repo/utils/privy-wallet";
import {
  normalizeVmId,
  type VmId,
  type VmPreference,
} from "~/lib/auth/vm-types";
import type { AppAuthUser, AppAuthWallet } from "./app-auth-types";

type WalletLike = {
  address: string;
  chainType?: string;
  connectorType?: string;
  walletClientType?: string;
  firstVerifiedAt?: Date | string | null;
  type?: string;
};

export type WalletUserLike = Pick<
  AppAuthUser,
  "linkedAccounts" | "wallet"
> | null;

export interface ResolvedWallet {
  address: string;
  chainType: string | null;
  isEmbedded: boolean;
  source: "embedded" | "external";
  vmId: VmId;
}

function areWalletAddressesEqual(left: string, right: string): boolean {
  const leftNormalized = left.trim();
  const rightNormalized = right.trim();

  const isEvmAddress = (value: string) => /^0x[a-fA-F0-9]{40}$/.test(value);
  if (isEvmAddress(leftNormalized) && isEvmAddress(rightNormalized)) {
    return leftNormalized.toLowerCase() === rightNormalized.toLowerCase();
  }

  return leftNormalized === rightNormalized;
}

function isWalletAccount(account: unknown): account is WalletLike {
  if (!account || typeof account !== "object") {
    return false;
  }

  const candidate = account as { type?: string; address?: unknown };
  return candidate.type === "wallet" && typeof candidate.address === "string";
}

function buildActiveWalletCandidate(
  activeWallet: AppAuthWallet,
  fallback: WalletLike | undefined,
): WalletLike {
  const chainType = activeWallet.chainType ?? fallback?.chainType;
  const connectorType = activeWallet.connectorType ?? fallback?.connectorType;
  const firstVerifiedAt =
    activeWallet.firstVerifiedAt ?? fallback?.firstVerifiedAt;
  const walletClientType =
    activeWallet.walletClientType ?? fallback?.walletClientType;

  return {
    address: fallback?.address ?? activeWallet.address,
    ...(chainType ? { chainType } : {}),
    ...(connectorType ? { connectorType } : {}),
    ...(firstVerifiedAt ? { firstVerifiedAt } : {}),
    ...(walletClientType ? { walletClientType } : {}),
  };
}

function getWalletCandidates(user: WalletUserLike): WalletLike[] {
  if (!user) {
    return [];
  }

  const linkedWallets: WalletLike[] = [];
  for (const account of user.linkedAccounts ?? []) {
    if (isWalletAccount(account)) {
      linkedWallets.push(account);
    }
  }

  const activeWallet = user.wallet?.address ? user.wallet : null;
  if (!activeWallet) {
    return linkedWallets;
  }

  const linkedMatches = linkedWallets
    .filter((wallet) =>
      areWalletAddressesEqual(wallet.address, activeWallet.address),
    )
    .sort(sortPrivyWalletsByPriority);
  const activeCandidate = buildActiveWalletCandidate(
    activeWallet,
    linkedMatches[0],
  );

  // Privy may expose the active wallet more than once in linkedAccounts with
  // different connection metadata (for example, the same address as both an
  // embedded wallet and a previously linked MetaMask wallet). The active
  // `user.wallet` record is the authoritative description for that address.
  return [
    activeCandidate,
    ...linkedWallets.filter(
      (wallet) =>
        !areWalletAddressesEqual(wallet.address, activeWallet.address),
    ),
  ];
}

export function resolvePrivyWallets(user: WalletUserLike): ResolvedWallet[] {
  const wallets = getWalletCandidates(user);

  return [...wallets].sort(sortPrivyWalletsByPriority).map((wallet) => {
    const isEmbedded = isPrivyEmbeddedWallet(wallet);
    const vmId = normalizeVmId(wallet.chainType, wallet.address);

    return {
      address: wallet.address,
      chainType: wallet.chainType ?? null,
      isEmbedded,
      source: isEmbedded ? "embedded" : "external",
      vmId,
    } satisfies ResolvedWallet;
  });
}

export function resolvePrimaryPrivyWallet(
  user: WalletUserLike,
  vmPreference: VmPreference = "auto",
): ResolvedWallet | null {
  const wallets = resolvePrivyWallets(user);

  if (wallets.length === 0) {
    return null;
  }

  if (vmPreference !== "auto") {
    const preferredWallet = wallets.find(
      (wallet) => wallet.vmId === vmPreference,
    );
    if (preferredWallet) {
      return preferredWallet;
    }

    if (vmPreference !== "evm" && vmPreference !== "solana") {
      return null;
    }
  }

  const activeWalletAddress = user?.wallet?.address;
  if (activeWalletAddress) {
    const activeWallet = wallets.find((wallet) =>
      areWalletAddressesEqual(wallet.address, activeWalletAddress),
    );
    if (activeWallet) {
      return activeWallet;
    }
  }

  return wallets[0] ?? null;
}
